import React, { useState } from 'react';
import axios from 'axios';
import './HeyGenTestComponent.css';

const HeyGenTestComponent = () => {
  const [formData, setFormData] = useState({
    apiKey: '',
    avatarId: '',
    voiceId: '',
    inputText: '',
    backgroundColor: '#FFFFFF',
    width: '1080',
    height: '1920'
  });

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [videoStatus, setVideoStatus] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const setPortraitPreset = (quality = '1080p') => {
    const presets = {
      '4k': { width: '2160', height: '3840' },
      '1080p': { width: '1080', height: '1920' },
      '720p': { width: '720', height: '1280' },
      '480p': { width: '480', height: '854' }
    };
    
    setFormData(prev => ({
      ...prev,
      width: presets[quality].width,
      height: presets[quality].height
    }));
  };

  const setLandscapePreset = (quality = '1080p') => {
    const presets = {
      '4k': { width: '3840', height: '2160' },
      '1080p': { width: '1920', height: '1080' },
      '720p': { width: '1280', height: '720' },
      '480p': { width: '854', height: '480' }
    };
    
    setFormData(prev => ({
      ...prev,
      width: presets[quality].width,
      height: presets[quality].height
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);
    setVideoStatus(null);

    try {
      const response = await axios.post('/api/heygen-test/create', formData);
      
      if (response.data.success) {
        setResult(response.data.data);
      } else {
        setError(response.data.message || 'Failed to create video');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const checkVideoStatus = async (videoId) => {
    setCheckingStatus(true);
    try {
      const url = formData.apiKey 
        ? `/api/heygen-test/status/${videoId}?apiKey=${encodeURIComponent(formData.apiKey)}`
        : `/api/heygen-test/status/${videoId}`;
        
      const response = await axios.get(url);
      
      if (response.data.success) {
        setVideoStatus(response.data.data);
      }
    } catch (err) {
      console.error('Status check error:', err);
    } finally {
      setCheckingStatus(false);
    }
  };

  return (
    <div className="heygen-test-container">
      <div className="heygen-test-card">
        <h2>HeyGen Video Generator Test</h2>
        <p className="subtitle">Test HeyGen API v2 Video Generation</p>

        <form onSubmit={handleSubmit} className="heygen-form">
          <div className="form-group">
            <label htmlFor="apiKey">HeyGen API Key *</label>
            <input
              type="password"
              id="apiKey"
              name="apiKey"
              value={formData.apiKey}
              onChange={handleInputChange}
              placeholder="Enter your HeyGen API key"
              required
            />
            <small>Get your API key from HeyGen dashboard → API Keys</small>
          </div>

          <div className="form-group">
            <label htmlFor="avatarId">Avatar ID *</label>
            <input
              type="text"
              id="avatarId"
              name="avatarId"
              value={formData.avatarId}
              onChange={handleInputChange}
              placeholder="e.g., avatar_id_here"
              required
            />
            <small>Get avatar IDs from HeyGen dashboard</small>
          </div>

          <div className="form-group">
            <label htmlFor="voiceId">Voice ID *</label>
            <input
              type="text"
              id="voiceId"
              name="voiceId"
              value={formData.voiceId}
              onChange={handleInputChange}
              placeholder="e.g., voice_id_here"
              required
            />
            <small>Get voice IDs from HeyGen dashboard</small>
          </div>

          <div className="form-group">
            <label htmlFor="inputText">Text to Speak *</label>
            <textarea
              id="inputText"
              name="inputText"
              value={formData.inputText}
              onChange={handleInputChange}
              placeholder="Enter the text you want the avatar to speak..."
              rows="4"
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="backgroundColor">Background Color</label>
              <input
                type="color"
                id="backgroundColor"
                name="backgroundColor"
                value={formData.backgroundColor}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="width">Width</label>
              <select
                id="width"
                name="width"
                value={formData.width}
                onChange={handleInputChange}
              >
                <optgroup label="Portrait (9:16)">
                  <option value="2160">2160 (4K Portrait)</option>
                  <option value="1080">1080 (Full HD Portrait)</option>
                  <option value="720">720 (HD Portrait)</option>
                  <option value="480">480 (SD Portrait)</option>
                </optgroup>
                <optgroup label="Landscape (16:9)">
                  <option value="3840">3840 (4K Landscape)</option>
                  <option value="1920">1920 (Full HD Landscape)</option>
                  <option value="1280">1280 (HD Landscape)</option>
                  <option value="854">854 (SD Landscape)</option>
                </optgroup>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="height">Height</label>
              <select
                id="height"
                name="height"
                value={formData.height}
                onChange={handleInputChange}
              >
                <optgroup label="Portrait (9:16)">
                  <option value="3840">3840 (4K Portrait)</option>
                  <option value="1920">1920 (Full HD Portrait)</option>
                  <option value="1280">1280 (HD Portrait)</option>
                  <option value="854">854 (SD Portrait)</option>
                </optgroup>
                <optgroup label="Landscape (16:9)">
                  <option value="2160">2160 (4K Landscape)</option>
                  <option value="1080">1080 (Full HD Landscape)</option>
                  <option value="720">720 (HD Landscape)</option>
                  <option value="480">480 (SD Landscape)</option>
                </optgroup>
              </select>
            </div>
          </div>

          <div className="preset-buttons">
            <label>Quick Presets:</label>
            <div className="preset-row">
              <button 
                type="button" 
                className="preset-btn portrait-btn"
                onClick={() => setPortraitPreset('1080p')}
              >
                📱 Portrait 1080p
              </button>
              <button 
                type="button" 
                className="preset-btn portrait-btn"
                onClick={() => setPortraitPreset('720p')}
              >
                📱 Portrait 720p
              </button>
              <button 
                type="button" 
                className="preset-btn landscape-btn"
                onClick={() => setLandscapePreset('1080p')}
              >
                🖥️ Landscape 1080p
              </button>
              <button 
                type="button" 
                className="preset-btn landscape-btn"
                onClick={() => setLandscapePreset('720p')}
              >
                🖥️ Landscape 720p
              </button>
            </div>
          </div>

          <button 
            type="submit" 
            className="submit-btn"
            disabled={loading}
          >
            {loading ? 'Generating Video...' : 'Generate Video'}
          </button>
        </form>

        {error && (
          <div className="alert alert-error">
            <strong>Error:</strong> {error}
          </div>
        )}

        {result && (
          <div className="result-section">
            <h3>Video Generation Result</h3>
            <div className="result-content">
              <pre>{JSON.stringify(result, null, 2)}</pre>
            </div>
            
            {result.video_id && (
              <button 
                onClick={() => checkVideoStatus(result.video_id)}
                className="status-btn"
                disabled={checkingStatus}
              >
                {checkingStatus ? 'Checking...' : 'Check Video Status'}
              </button>
            )}
          </div>
        )}

        {videoStatus && (
          <div className="status-section">
            <h3>Video Status</h3>
            <div className="status-content">
              <pre>{JSON.stringify(videoStatus, null, 2)}</pre>
            </div>
          </div>
        )}

        <div className="info-section">
          <h4>📚 Documentation</h4>
          <p>
            <a 
              href="https://docs.heygen.com/reference/create-an-avatar-video-v2" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              HeyGen API v2 Documentation
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default HeyGenTestComponent;
